// Pagination.tsx — Co Marca — estilo plataforma
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext.tsx';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    itemsPerPage: number;
    totalItems: number;
}

const colors = {
    primary:        '#61257B',
    primaryDark:    '#451759',
    secondary:      '#C1348B',
    secondaryLight: '#FFD97C',
    accentDark:     '#EA8406',
};

const getCss = (isDark: boolean) => `

.pag-wrap * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

.pag-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 9px;
    border: 1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.14)' : '#e2e8f0'};
    background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff'};
    font-size: .78rem; font-weight: 700;
    color: ${isDark ? '#ffffff' : '#374151'};
    cursor: pointer; transition: all .2s cubic-bezier(.16,1,.3,1);
    font-family: 'DM Sans', sans-serif;
}
.pag-btn:hover:not(:disabled) {
    background: ${isDark ? 'rgba(97, 37, 123, 0.3)' : `${colors.primary}10`};
    border-color: ${colors.secondary};
    color: ${isDark ? '#ffd97c' : colors.primary};
    transform: translateY(-1px);
}
.pag-btn:disabled {
    background: ${isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
    color: ${isDark ? 'rgba(255, 255, 255, 0.25)' : '#cbd5e1'};
    border-color: ${isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9'};
    cursor: not-allowed; transform: none;
}
.pag-btn-active {
    background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary}) !important;
    border-color: transparent !important;
    color: #fff !important;
    box-shadow: 0 4px 14px rgba(97, 37, 123, 0.45) !important;
}
.pag-btn-active:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(193, 52, 139, 0.5) !important;
}
.pag-nav {
    display: inline-flex; align-items: center; gap: 6px;
    background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff'};
    border: 1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.14)' : '#e2e8f0'};
    border-radius: 11px;
    padding: 5px 12px; font-size: .78rem; font-weight: 700;
    color: ${isDark ? '#ffffff' : '#374151'};
    cursor: pointer; transition: all .2s;
    font-family: 'DM Sans', sans-serif;
}
.pag-nav:hover:not(:disabled) {
    background: ${isDark ? 'rgba(97, 37, 123, 0.25)' : '#f8fafc'};
    border-color: ${colors.secondary};
    color: ${isDark ? '#ffd97c' : colors.primary};
}
.pag-nav:disabled {
    background: ${isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
    color: ${isDark ? 'rgba(255, 255, 255, 0.25)' : '#cbd5e1'};
    border-color: ${isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9'};
    cursor: not-allowed;
}
`;

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange, itemsPerPage, totalItems }) => {
    const { isDark } = useTheme();
    const from = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
    const to   = Math.min(currentPage * itemsPerPage, totalItems);

    // Generar páginas visibles (máx 5, con puntos suspensivos)
    const getPages = (): (number | '...')[] => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const pages: (number | '...')[] = [];
        if (currentPage <= 4) {
            pages.push(1, 2, 3, 4, 5, '...', totalPages);
        } else if (currentPage >= totalPages - 3) {
            pages.push(1, '...', totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages);
        } else {
            pages.push(1, '...', currentPage-1, currentPage, currentPage+1, '...', totalPages);
        }
        return pages;
    };

    return (
        <>
            <style>{getCss(isDark)}</style>
            <div className="pag-wrap" style={{ padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>

                {/* Info izquierda */}
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ display:'flex', gap:'3px' }}>
                        {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c,i)=>(
                            <div key={i} style={{ height:'2px', width:i===0?'12px':'5px', borderRadius:'2px', background:c, opacity:.8 }}/>
                        ))}
                    </div>
                    <span style={{ fontSize:'.75rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight:'600' }}>
                        Mostrando{' '}
                        <span style={{ color: isDark ? '#ffd97c' : '#0f172a', fontWeight:'650' }}>{from}–{to}</span>
                        {' '}de{' '}
                        <span style={{ color: isDark ? '#ffd97c' : '#0f172a', fontWeight:'650' }}>{totalItems}</span>
                        {' '}registros
                    </span>
                </div>

                {/* Controles derecha */}
                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    {/* Anterior */}
                    <button
                        className="pag-nav"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <ChevronLeft size={13}/> Anterior
                    </button>

                    {/* Páginas numeradas */}
                    <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                        {getPages().map((page, idx) =>
                            page === '...' ? (
                                <span key={`dots-${idx}`} style={{ width:'32px', textAlign:'center', fontSize:'.78rem', color: isDark ? '#64748b' : '#94a3b8', fontWeight:'700' }}>···</span>
                            ) : (
                                <button
                                    key={page}
                                    className={`pag-btn ${page === currentPage ? 'pag-btn-active' : ''}`}
                                    onClick={() => onPageChange(page as number)}
                                >
                                    {page}
                                </button>
                            )
                        )}
                    </div>

                    {/* Siguiente */}
                    <button
                        className="pag-nav"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Siguiente <ChevronRight size={13}/>
                    </button>
                </div>
            </div>
        </>
    );
};

export default Pagination;
